/**
 * ComingSoonContext — iter-60.
 *
 * Global gate for "not yet released" experiences (Spa & Hammam, Memberships).
 *
 * Any component (nav links, route wrappers) can call `open(label)` to display
 * a premium modal informing the visitor that the experience will be available
 * soon. The modal exposes two CTAs :
 *   • Fermer                       → simply closes the modal
 *   • Découvrir nos autres         → closes + scrolls to the homepage
 *     expériences                    "Choisissez votre expérience" section
 *
 * Text is currently hardcoded ; it can later be exposed via `site_config` to
 * be editable from the staff dashboard.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { useSiteConfig, sel } from "../lib/site-config";

const Ctx = createContext(null);

export function ComingSoonProvider({ children }) {
  const [state, setState] = useState({ open: false, label: null });
  const navigate = useNavigate();
  const loc = useLocation();
  const cfg = useSiteConfig();
  const cms = sel.comingSoon(cfg);

  const open = useCallback((label) => setState({ open: true, label }), []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  // Lock body scroll while open.
  useEffect(() => {
    if (!state.open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [state.open, close]);

  const goToExperiences = useCallback(() => {
    close();
    if (loc.pathname === "/") {
      const el = document.getElementById("experiences");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#experiences");
    }
  }, [close, loc.pathname, navigate]);

  const value = useMemo(() => ({ open, close, isOpen: state.open, label: state.label }), [open, close, state.open, state.label]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {state.open && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-6 animate-fade-in"
          onClick={close}
          data-testid="coming-soon-modal"
          role="dialog"
          aria-modal="true"
          aria-label={state.label ? `${state.label} — Bientôt disponible` : "Bientôt disponible"}
        >
          <div className="absolute inset-0 bg-black/55 backdrop-blur-md" />

          <div
            className="relative w-full max-w-md bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.55)] animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Fermer"
              className="absolute top-4 right-4 p-1.5 text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors"
              data-testid="coming-soon-close-icon"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            <div className="p-8 sm:p-10 text-center">
              <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-4">
                {state.label ? `· ${state.label} ·` : "· Life is Here ·"}
              </div>
              <h3 className="font-serif font-light text-3xl sm:text-4xl leading-[1.05] text-[#0A0A0A] mb-5">
                {cms.title || "Très bientôt disponible"}
              </h3>
              <p className="text-sm md:text-base text-[#0A0A0A]/65 leading-relaxed font-light max-w-sm mx-auto mb-8">
                {cms.body || "Nous finalisons actuellement cette expérience afin de vous offrir un service à la hauteur des standards de Boulay Beach Resort. Elle sera disponible très prochainement. Merci pour votre patience."}
              </p>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={goToExperiences}
                  className="group inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#B8922A] text-white px-6 py-3.5 text-[0.68rem] tracking-[0.32em] uppercase transition-colors"
                  data-testid="coming-soon-cta-discover"
                >
                  {cms.cta_primary || "Découvrir nos autres expériences"}
                  <ArrowRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex items-center justify-center gap-2 text-[0.68rem] tracking-[0.32em] uppercase text-[#0A0A0A]/60 hover:text-[#0A0A0A] pt-1"
                  data-testid="coming-soon-cta-close"
                >
                  {cms.cta_secondary || "Fermer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useComingSoon() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useComingSoon must be used inside <ComingSoonProvider>");
  return ctx;
}

/** Paths (or path prefixes) that must trigger the modal instead of navigating. */
const BLOCKED_MATCHERS = [
  { test: (p) => p === "/univers/spa", label: "Spa & Hammam" },
  { test: (p) => p.startsWith("/memberships"), label: "Membership" },
];

/**
 * Return `{ blocked, label }` for a given target path (e.g. from a Link `to` prop
 * or a hash link "/memberships#royal").
 */
export function checkComingSoon(to) {
  if (!to) return { blocked: false, label: null };
  const path = String(to).split("#")[0].split("?")[0];
  for (const m of BLOCKED_MATCHERS) {
    if (m.test(path)) return { blocked: true, label: m.label };
  }
  return { blocked: false, label: null };
}

/**
 * Route-level gate. Rendered as the `element` of a Route whose real page must
 * remain temporarily inaccessible (e.g. `/univers/spa`, `/memberships`).
 * On mount it opens the modal and redirects the user to the homepage so the
 * closing action lands them on a live experience.
 *
 * The original page component MUST NOT be removed from the codebase — the
 * developer just swaps this component in the route definition until the
 * experience is ready to launch.
 */
export function GatedRoute({ label }) {
  const { open } = useComingSoon();
  const navigate = useNavigate();
  useEffect(() => {
    open(label);
    // Redirect to home so the modal closing action lands on a live page.
    navigate("/", { replace: true });
  }, [label, open, navigate]);
  return null;
}
